import type { CompanyContentBoostType } from "@/modules/companies/client/api/contentBoostTypes"
import type { AutocompleteSearchProperties } from "@/modules/jobAds/client/components/AutocompleteSearchInput/AutocompleteSearchInput"
import type { PageContextValue } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import type { FoLayoutProps } from "@/modules/layouts/components/FoLayout"
import type { ParsedUrlQuery } from "querystring"
import type { FunctionComponent } from "react"

import classNames from "classnames"
import { useContext, useEffect, useMemo, useState } from "react"
import { NIL as NIL_UUID } from "uuid"

import { CCTheme } from "@/modules/careerCenter/components/CCTheme"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { AlgoliaRequestParamsRetriever } from "@/modules/jobAds/client/components/AlgoliaRequestParamsRetriever/AlgoliaRequestParamsRetriever"
import { JobAdsAside } from "@/modules/jobAds/client/components/Layout/JobAdsAside/JobAdsAside"
import { JobAdsFilters } from "@/modules/jobAds/client/components/Layout/JobAdsFilters/JobAdsFilters"
import { JobAdsResults } from "@/modules/jobAds/client/components/Layout/JobAdsResults/JobAdsResults"
import { MetaData } from "@/modules/jobAds/client/components/MetaData/MetaData"
import {
  JOB_ADS_FO_ALGOLIA_DEBUG,
  JOB_ADS_FO_SAVED_SEARCH_NEW_JOB_ADS_SINCE_LAST_SEEN,
  JOB_ADS_FO_SAVED_SEARCH_NEW_JOB_ADS_SINCE_LAST_SEEN_VARIANTS,
  JOB_ADS_FO_WOOSMAP_MIGRATION,
  JOB_ALERT_FROM_SAVED_SEARCH,
} from "@/modules/jobAds/client/constants/flags"
import { useGetUserSavedSearch } from "@/modules/jobAds/client/hooks/useGetUserSavedSearch"
import { useJobAdsClientTracking } from "@/modules/jobAds/client/hooks/useJobAdsClientTracking"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { PageContextProvider } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { PrefilteredFooter } from "@/modules/jobSearchPrefiltered/components/PrefilteredFooter/PrefilteredFooter"
import { PrefilteredHeader } from "@/modules/jobSearchPrefiltered/components/PrefilteredHeader/PrefilteredHeader"
import { useContent } from "@/modules/jobSearchPrefiltered/hooks/useContent"
import { usePrefilteredJobSearch } from "@/modules/jobSearchPrefiltered/hooks/usePrefilteredJobSearch"
import { getLocalizedContentKeysForSlug } from "@/modules/jobSearchPrefiltered/utils/getLocalizedContentKeysForSlug"
import { FoLayout } from "@/modules/layouts/components/FoLayout"
import { Layout as MainLayout } from "@/modules/layouts/components/Layout/Layout"
import { LayoutChild } from "@/modules/layouts/components/Layout/LayoutChild"
import { useLocale } from "@/modules/locales/useLocale"
import { useRouter } from "@/modules/routing/hooks/useRouter"
import {
  DIRECT_ALGOLIA_CONNECTION_UNLEASH_TOGGLE,
  SEARCH_PROXY_V3_CONNECTION_UNLEASH_TOGGLE,
} from "@/modules/search/searchEngine"
import { ServerContext } from "@/modules/server/context"
import { useTracking } from "@/modules/tracking/useTracking"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"
import { useCurrentUserSearchConfigFetch } from "@/modules/user/client/hooks/useCurrentUserSearchConfigFetch"

import "react-loading-skeleton/dist/skeleton.css"
import styles from "./Layout.module.css"

export type LayoutProps = {
  hostname: FoLayoutProps["host"]
  prefilteredSearchParams: ParsedUrlQuery
}

export const Layout: FunctionComponent<LayoutProps> = ({ hostname, prefilteredSearchParams }) => {
  const { t } = useTranslation(["ai_cover_letter", "job_ads_fo", "job_search_prefiltered"])
  const locale = useLocale()
  const { baseUrl } = useContext(ServerContext) || { baseUrl: "" }
  const { data: contentData } = useContent()
  const tracking = useTracking()
  const { push } = useRouter()

  const searchParams = useJobAdsSearchParams()
  const isAlgoliaDebug = useFlag(JOB_ADS_FO_ALGOLIA_DEBUG)

  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCurrentUserSearchConfig } = useCurrentUserSearchConfigFetch()

  const { trackJobAdsSearch } = useJobAdsClientTracking()
  const isPrefilteredJobSearchPage = usePrefilteredJobSearch()
  const { dataJobAds } = useJobAdsFromAlgolia<CompanyContentBoostType>()
  const { data: savedSearch } = useGetUserSavedSearch({
    isUserLogged: dataCurrentUser?.isLogged || false,
  })

  const [autocompleteState, setAutocompleteState] = useState<AutocompleteSearchProperties>()

  const search = searchParams.toString()

  const algoliaSearchId = dataJobAds?.searchId
  const nbResults = dataJobAds?.totalSize || 0
  const pageSize = dataJobAds?.pageSize || 0
  const exhaustiveFacets = dataJobAds?.exhaustiveFacets || true
  const exhaustiveResults = dataJobAds?.exhaustiveResults || true
  const careerCenterId = dataCareerCenter?.id || NIL_UUID
  const userId = dataCurrentUser?.id

  const isDirectAlgoliaConnectionEnabled = useFlag(DIRECT_ALGOLIA_CONNECTION_UNLEASH_TOGGLE)
  const isJobAdsFoWoosmapMigrationEnabled = useFlag(JOB_ADS_FO_WOOSMAP_MIGRATION)
  const isSearchProxyV3ConnectionEnabled = useFlag(SEARCH_PROXY_V3_CONNECTION_UNLEASH_TOGGLE)
  const isJobAlertFromSavedSearchFeatureEnabled = useFlag(JOB_ALERT_FROM_SAVED_SEARCH)
  const isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled = useFlag(
    JOB_ADS_FO_SAVED_SEARCH_NEW_JOB_ADS_SINCE_LAST_SEEN,
    JOB_ADS_FO_SAVED_SEARCH_NEW_JOB_ADS_SINCE_LAST_SEEN_VARIANTS.ENABLED
  )
  const pageContextValue: PageContextValue = useMemo(
    (): PageContextValue => ({
      careerCenterConf: dataCareerCenter?.careerCenterConf || null,
      careerCenterId: dataCareerCenter?.id || null,
      careerCenterLogo: dataCareerCenter?.logo || null,
      careerCenterMainId: dataCareerCenter?.mainCareerCenterId || null,
      careerCenterShortName: dataCareerCenter?.shortName || null,
      currentUserSearchConfig: dataCurrentUserSearchConfig || null,
      facets: dataJobAds?.facets || [],
      hasUserDailyJobOffersAlert: dataCurrentUser?.attributes.dailyJobOffersAlert || false,
      isDirectAlgoliaConnectionEnabled,
      isJobAdsFoWoosmapMigrationEnabled,
      isJobAlertFromSavedSearchFeatureEnabled,
      isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled,
      isSearchProxyV3ConnectionEnabled,
      isUserLogged: dataCurrentUser?.isLogged || false,
      onSuggestionClicked: setAutocompleteState,
      prefilteredSearchParams,
      push,
      savedSearch,
      savedSearchSize: dataJobAds?.savedSearchSize || 0,
      trackingContext: {
        careerCenterId,
        exhaustiveFacets,
        exhaustiveResults,
        pageSize,
        searchId: algoliaSearchId || NIL_UUID,
        totalSize: nbResults,
        userId,
      },
    }),
    [
      algoliaSearchId,
      careerCenterId,
      dataCareerCenter?.careerCenterConf,
      dataCareerCenter?.id,
      dataCareerCenter?.logo,
      dataCareerCenter?.mainCareerCenterId,
      dataCareerCenter?.shortName,
      dataCurrentUser?.attributes.dailyJobOffersAlert,
      dataCurrentUser?.isLogged,
      dataCurrentUserSearchConfig,
      dataJobAds?.facets,
      dataJobAds?.savedSearchSize,
      exhaustiveFacets,
      exhaustiveResults,
      isDirectAlgoliaConnectionEnabled,
      isJobAdsFoWoosmapMigrationEnabled,
      isJobAlertFromSavedSearchFeatureEnabled,
      isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled,
      isSearchProxyV3ConnectionEnabled,
      nbResults,
      pageSize,
      prefilteredSearchParams,
      push,
      savedSearch,
      userId,
    ]
  )

  // we excluded the trackJobAdsSearch from the useMemo deps because we want to memoize the function
  // relative to the search result only (and avoid tracking call duplicates)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedTrackJobAdsSearch = useMemo(() => trackJobAdsSearch, [tracking, search])

  useEffect(() => {
    const isAnAutocompleteSearch = searchParams.get("autocomplete_search")

    if (isAnAutocompleteSearch && autocompleteState) {
      memoizedTrackJobAdsSearch({
        algoliaSearchId,
        autocompleteSearch: {
          keyword_searched: autocompleteState.keywordSearched,
          rank_suggestion_clicked: autocompleteState.rankSuggestionClicked,
          suggestion_clicked: autocompleteState.suggestionClicked,
        },
        nbResults,
      })
      return
    }

    memoizedTrackJobAdsSearch({ algoliaSearchId, nbResults })

    // searchParams changes on every render so we use its stringified output
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, algoliaSearchId, nbResults, memoizedTrackJobAdsSearch])

  return (
    <PageContextProvider value={pageContextValue}>
      <CCTheme>
        {!isPrefilteredJobSearchPage && <MetaData />}
        <FoLayout host={hostname}>
          <MainLayout>
            <LayoutChild className={classNames(styles.filtersLayout, styles.filtersContainer)} variant="full">
              <PrefilteredHeader />

              <JobAdsFilters />
            </LayoutChild>
          </MainLayout>

          <MainLayout className={styles.resultsLayout}>
            <JobAdsResults />
            <JobAdsAside />
          </MainLayout>

          {isPrefilteredJobSearchPage && contentData && (
            <PrefilteredFooter
              locale={locale}
              baseUrl={baseUrl}
              homePageLabel={t("job_search_prefiltered.breadcrumb.go_to_homepage")}
              parentPageLabel={t("job_search_prefiltered.breadcrumb.go_to_transac_pages_index")}
              currentSlugPageLabel={t(
                getLocalizedContentKeysForSlug(contentData.currentSlug).title,
                contentData?.currentSlug.params
              )}
            />
          )}
        </FoLayout>

        {isAlgoliaDebug && <AlgoliaRequestParamsRetriever />}
      </CCTheme>
    </PageContextProvider>
  )
}
